import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
    MagnifyingGlassIcon,
    FunnelIcon,
    UsersIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ExclamationTriangleIcon,
    ArrowPathIcon,
    MapPinIcon,
    TruckIcon,
    BoltIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { getActiveDrivers } from "../api/liveMonitoringApi";
import type { ActiveDriversQueryParams, DriverOnlineStatus } from "../types";

// ============================================================================
// Constants
// ============================================================================

const statusOptions: { value: DriverOnlineStatus | ""; label: string }[] = [
    { value: "", label: "All Status" },
    { value: "AVAILABLE", label: "Available" },
    { value: "ON_TRIP", label: "On Trip" },
];

// ============================================================================
// Badges
// ============================================================================

const StatusBadge = ({ status }: { status: string }) => {
    return status === "AVAILABLE" ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Available
        </span>
    ) : (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            On Trip
        </span>
    );
};

// ============================================================================
// Helpers
// ============================================================================

const formatTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            hour: "2-digit", minute: "2-digit", second: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

// ============================================================================
// Component
// ============================================================================

export const LiveDriversRoute = () => {
    const navigate = useNavigate();
    const [showFilters, setShowFilters] = useState(false);
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<ActiveDriversQueryParams>({
        status: "",
    });

    const params: ActiveDriversQueryParams = {
        ...filters,
        status: filters.status || undefined,
    };

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ["live-drivers", params],
        queryFn: () => getActiveDrivers(params),
        refetchInterval: 15_000,
    });

    const drivers = data?.data?.drivers ?? [];
    const totalOnline = data?.data?.totalOnline ?? 0;
    const available = data?.data?.available ?? 0;
    const onTrip = data?.data?.onTrip ?? 0;

    const filtered = search
        ? drivers.filter(
            (d) =>
                d.firstName?.toLowerCase().includes(search.toLowerCase()) ||
                d.lastName?.toLowerCase().includes(search.toLowerCase()) ||
                d.email?.toLowerCase().includes(search.toLowerCase()) ||
                d.vehicle?.plateNumber?.toLowerCase().includes(search.toLowerCase())
        )
        : drivers;

    // ========================================================================
    // Loading / Error
    // ========================================================================

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                <h3 className="mt-3 text-lg font-semibold text-rose-700 dark:text-rose-300">Failed to load active drivers</h3>
                <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">{(error as Error).message}</p>
            </div>
        );
    }

    // ========================================================================
    // Render
    // ========================================================================

    return (
        <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-emerald-50 p-2 dark:bg-emerald-900/20">
                            <BoltIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Online</p>
                            <p className="text-xl font-bold text-emerald-700 dark:text-emerald-300">{totalOnline}</p>
                        </div>
                    </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-blue-50 p-2 dark:bg-blue-900/20">
                            <UsersIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Available</p>
                            <p className="text-xl font-bold text-blue-700 dark:text-blue-300">{available}</p>
                        </div>
                    </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-amber-50 p-2 dark:bg-amber-900/20">
                            <TruckIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">On Trip</p>
                            <p className="text-xl font-bold text-amber-700 dark:text-amber-300">{onTrip}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search drivers…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    />
                </div>
                <Button variant="secondary" size="sm" onClick={() => setShowFilters(!showFilters)}>
                    <FunnelIcon className="mr-1.5 h-4 w-4" /> Filters
                </Button>
                <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isFetching}>
                    <ArrowPathIcon className={`mr-1.5 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                    <select
                        value={filters.status ?? ""}
                        onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value as DriverOnlineStatus | "" }))}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    >
                        {statusOptions.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* Table */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/50">
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Driver</th>
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Status</th>
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Vehicle</th>
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Location</th>
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Speed</th>
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Last Update</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-12 text-center">
                                        <UsersIcon className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
                                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">No active drivers found</p>
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((driver) => (
                                    <tr
                                        key={driver.driverId}
                                        onClick={() => navigate(`/live-monitoring/drivers/${driver.driverId}`)}
                                        className="cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    >
                                        <td className="px-4 py-3">
                                            <div>
                                                <p className="font-medium text-slate-900 dark:text-white">{driver.firstName} {driver.lastName}</p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">{driver.email}</p>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <StatusBadge status={driver.status} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div>
                                                <p className="text-sm text-slate-900 dark:text-white">{driver.vehicle?.plateNumber}</p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                                    {driver.vehicle?.color} {driver.vehicle?.make} {driver.vehicle?.model}
                                                </p>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-300">
                                                <MapPinIcon className="h-3.5 w-3.5 text-slate-400" />
                                                {driver.latitude?.toFixed(4)}, {driver.longitude?.toFixed(4)}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                                            {driver.speed != null ? `${driver.speed.toFixed(1)} km/h` : "—"}
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                                            {formatTime(driver.lastUpdatedAt)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="border-t border-slate-200 px-4 py-3 dark:border-slate-800">
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        {filtered.length} driver{filtered.length !== 1 ? "s" : ""} shown · Auto-refreshing every 15s
                    </p>
                </div>
            </div>
        </div>
    );
};
